/** Positioning (legacy "one-click chicken") state and device bridge. */
import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { hidManager } from '@/api/device/hid'
import { buildEqModeCommand, buildPositioningCurveCommands, type EqDataPoint } from '@/api/device/commands'

export type ChijiPresetType = 'system' | 'custom'
export interface ChijiSettings { footsteps: number; gunshots: number }
export interface ChijiPreset {
  id: string; name: string; type: ChijiPresetType; settings: ChijiSettings; isPinned?: boolean
  gunPoints: EqDataPoint[]; footstepPoints: EqDataPoint[]; gunLevel: number; footstepLevel: number
}

const FOOTSTEP_FREQS = [2000, 3000, 6000, 8000, 10000, 12000, 16000]
const GUNSHOT_FREQS = [20, 50, 75, 150, 300, 600, 1000]
const LEVEL_GAIN = [-9, -3, -6, 0, 3, 6, 9]
const MAX_CUSTOM = 20
function point(freq: number, fid: number, gain = 0): EqDataPoint { return { fid, freq: freq >= 1000 ? `${freq / 1000}K` : `${freq}Hz`, gain, q: '0.75' } }
function points(freqs: number[]): EqDataPoint[] { return freqs.map((freq, i) => point(freq, i + 1)) }
function settings(value?: Partial<ChijiSettings>): ChijiSettings {
  return { footsteps: Math.max(-3, Math.min(3, Math.round(Number(value?.footsteps ?? 2)))), gunshots: Math.max(-3, Math.min(3, Math.round(Number(value?.gunshots ?? -3)))) }
}
function preset(id: string, name: string, type: ChijiPresetType, value: ChijiSettings, pinned = false): ChijiPreset {
  return { id, name, type, settings: settings(value), isPinned: pinned, gunPoints: points(GUNSHOT_FREQS), footstepPoints: points(FOOTSTEP_FREQS), gunLevel: value.gunshots + 3, footstepLevel: value.footsteps + 3 }
}

export const useChijiStore = defineStore('chiji', () => {
  const enabled = ref(false)
  const hasEverOpened = ref(false)
  const currentPresetId = ref('initial')
  const appliedPresetId = ref<string | null>(null)
  const customPresets = ref<ChijiPreset[]>([])
  const orderedIds = ref<string[]>(['initial'])
  let localActionGuardUntil = 0
  const allPresets = computed(() => {
    const map = new Map<string, ChijiPreset>([['initial', preset('initial', 'Preset', 'system', { footsteps: 2, gunshots: -3 })]])
    customPresets.value.forEach(item => map.set(item.id, item))
    return orderedIds.value.map(id => map.get(id)).filter(Boolean) as ChijiPreset[]
  })
  const currentPreset = computed(() => allPresets.value.find(item => item.id === currentPresetId.value) || allPresets.value[0])
  const isCustomPreset = computed(() => currentPreset.value?.type === 'custom')
  const tagValue = computed(() => Math.max(0, orderedIds.value.indexOf(currentPresetId.value)))

  function persist(): void {
    if (localStorage.getItem('isLoggedIn') !== 'true') return
    localStorage.setItem('chijiSettings', JSON.stringify({ enabled: enabled.value, hasEverOpened: hasEverOpened.value, currentPresetId: currentPresetId.value, orderedIds: orderedIds.value, customPresets: customPresets.value }))
  }
  function loadFromStorage(): void {
    if (localStorage.getItem('isLoggedIn') !== 'true') return
    try {
      const saved = JSON.parse(localStorage.getItem('chijiSettings') || '{}')
      customPresets.value = Array.isArray(saved.customPresets) ? saved.customPresets.map((item: ChijiPreset) => preset(item.id, item.name, 'custom', item.settings, !!item.isPinned)) : []
      const customIds = customPresets.value.map(item => item.id)
      orderedIds.value = ['initial', ...(Array.isArray(saved.orderedIds) ? saved.orderedIds.filter((id: string) => customIds.includes(id)) : customIds)]
      currentPresetId.value = orderedIds.value.includes(saved.currentPresetId) ? saved.currentPresetId : 'initial'
      enabled.value = saved.enabled === true; hasEverOpened.value = saved.hasEverOpened === true
    } catch { /* ignore malformed state */ }
  }
  function updatePoints(item: ChijiPreset): void {
    const gunGain = LEVEL_GAIN[item.settings.gunshots + 3] ?? 0
    const footGain = LEVEL_GAIN[item.settings.footsteps + 3] ?? 0
    item.gunLevel = item.settings.gunshots + 3; item.footstepLevel = item.settings.footsteps + 3
    item.gunPoints = points(GUNSHOT_FREQS).map((p, i) => ({ ...p, gain: i < 4 ? gunGain : 0 }))
    item.footstepPoints = points(FOOTSTEP_FREQS).map((p, i) => ({ ...p, gain: i < 2 ? footGain : 0 }))
  }
  async function sendProtocol(): Promise<void> {
    if (!hidManager.isConnected()) return
    const eqStore = await import('./eq').then(module => module.useEqStore())
    const mode = eqStore.appliedMode || eqStore.activeMode
    const eqIndex = eqStore.isEnabled && eqStore.appliedPresetId ? Math.max(0, eqStore.currentModeState.systemPresets.concat(eqStore.currentModeState.customPresets).findIndex(item => item.id === eqStore.appliedPresetId)) : 0
    await hidManager.sendData(buildEqModeCommand(eqStore.isEnabled, ({ game: 0, music: 1, movie: 2 } as Record<string, number>)[mode], eqIndex, enabled.value, tagValue.value), false)
  }
  async function sendCurve(item = currentPreset.value): Promise<void> {
    if (!item || !hidManager.isConnected()) return
    updatePoints(item)
    for (const command of buildPositioningCurveCommands(item.footstepPoints, item.gunPoints)) {
      await hidManager.sendData(command, false)
      await new Promise(resolve => setTimeout(resolve, 30))
    }
  }
  async function setEnabled(value: boolean): Promise<void> {
    localActionGuardUntil = Date.now() + 1200
    enabled.value = value; hasEverOpened.value = hasEverOpened.value || value
    if (value) {
      const eqStore = await import('./eq').then(module => module.useEqStore())
      if (eqStore.isEnabled) await eqStore.setEqEnabled(false)
      appliedPresetId.value = currentPresetId.value; await sendProtocol(); await sendCurve()
    } else { appliedPresetId.value = null; await sendProtocol() }
    persist()
  }
  async function selectPreset(id: string): Promise<void> {
    if (!orderedIds.value.includes(id)) return
    localActionGuardUntil = Date.now() + 1200
    currentPresetId.value = id
    if (enabled.value) { appliedPresetId.value = id; await sendProtocol(); await sendCurve() }
    persist()
  }
  async function setLevel(key: keyof ChijiSettings, value: number): Promise<void> {
    if (!enabled.value || !currentPreset.value) return
    currentPreset.value.settings[key] = Math.max(-3, Math.min(3, Math.round(value)))
    updatePoints(currentPreset.value); await sendCurve(currentPreset.value); persist()
  }
  const setGunLevel = (level: number) => setLevel('gunshots', level - 3)
  const setFootstepLevel = (level: number) => setLevel('footsteps', level - 3)
  function addCustomPreset(name: string, base?: ChijiPreset): ChijiPreset | null {
    if (customPresets.value.length >= MAX_CUSTOM) return null
    const id = `custom-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
    const item = preset(id, name.trim() || `Custom ${customPresets.value.length + 1}`, 'custom', base?.settings || { footsteps: 0, gunshots: 0 })
    customPresets.value.push(item); orderedIds.value.push(id); currentPresetId.value = id; persist(); return item
  }
  function renamePreset(id: string, name: string): boolean { const item = customPresets.value.find(p => p.id === id); if (!item || !name.trim()) return false; item.name = name.trim().slice(0, 20); persist(); return true }
  function deleteCustomPreset(id: string): boolean { const index = customPresets.value.findIndex(item => item.id === id); if (index < 0) return false; customPresets.value.splice(index, 1); orderedIds.value = orderedIds.value.filter(item => item !== id); currentPresetId.value = 'initial'; if (enabled.value) void selectPreset('initial'); persist(); return true }
  async function resetPreset(id = currentPresetId.value): Promise<void> { const item = allPresets.value.find(p => p.id === id); if (!item) return; item.settings = item.type === 'system' ? { footsteps: 2, gunshots: -3 } : { footsteps: 0, gunshots: 0 }; updatePoints(item); if (enabled.value && id === currentPresetId.value) await sendCurve(item); persist() }
  function pinCurrentPreset(): void { const item = currentPreset.value; if (!item) return; customPresets.value.forEach(p => { p.isPinned = p.id === item.id }); orderedIds.value = [item.id, ...orderedIds.value.filter(id => id !== item.id)]; persist() }
  function syncHardwareStatus(value: { enabled: boolean; tag: number }): void {
    if (Date.now() < localActionGuardUntil) return
    enabled.value = value.enabled
    const id = orderedIds.value[Math.max(0, Math.min(orderedIds.value.length - 1, value.tag))] || 'initial'
    currentPresetId.value = id; appliedPresetId.value = value.enabled ? id : null; hasEverOpened.value = hasEverOpened.value || value.enabled
  }

  return { enabled, hasEverOpened, currentPresetId, appliedPresetId, customPresets, allPresets, currentPreset, isCustomPreset, tagValue, maxCustomPresets: MAX_CUSTOM, currentPresetSettings: computed(() => currentPreset.value?.settings), setEnabled, selectPreset, setGunLevel, setFootstepLevel, addCustomPreset, renamePreset, deleteCustomPreset, resetPreset, pinCurrentPreset, loadFromStorage, saveToStorage: persist, applyCurrentPreset: sendCurve, syncHardwareStatus }
})
